
import { FeatureGate } from '../components/FeatureGate';
import { FeatureNotAvailablePage } from './FeatureNotAvailablePage';

/**
 * Advanced Search page - only available when ADVANCED_SEARCH_ENABLED flag is true
 */
export function AdvancedSearchPage() {
  return (
    <FeatureGate 
      flag="ADVANCED_SEARCH_ENABLED"
      fallback={<FeatureNotAvailablePage featureName="Advanced Search" />}
    >
      <div className="min-h-screen bg-gradient-to-br from-teddy via-white to-teddy/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-urban mb-4">Advanced Search</h1>
            <p className="text-urban/70 max-w-2xl mx-auto">
              Use advanced filters and search capabilities to find exactly what you're looking for.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-urban/10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Search Filters */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-urban">Search Filters</h3>
                
                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Item Category
                  </label>
                  <select className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent">
                    <option value="">All Categories</option>
                    <option value="furniture">Furniture</option>
                    <option value="electronics">Electronics</option>
                    <option value="books">Books</option>
                    <option value="clothing">Clothing</option>
                    <option value="toys">Toys</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Condition
                  </label>
                  <select className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent">
                    <option value="">Any Condition</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Distance (km)
                  </label>
                  <input 
                    type="range" 
                    min="1" 
                    max="50" 
                    defaultValue="10"
                    className="w-full h-2 bg-urban/20 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-urban/60 mt-1">
                    <span>1km</span>
                    <span>50km</span>
                  </div>
                </div>
              </div>

              {/* Date Filters */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-urban">Date Range</h3>
                
                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    From Date
                  </label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    To Date
                  </label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Status
                  </label>
                  <div className="space-y-2">
                    {['Available', 'Going Soon', 'Gone'].map((status) => (
                      <label key={status} className="flex items-center">
                        <input 
                          type="checkbox" 
                          className="rounded border-urban/20 text-cardboard focus:ring-cardboard"
                          defaultChecked={status === 'Available'}
                        />
                        <span className="ml-2 text-sm text-urban">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Advanced Options */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-urban">Advanced Options</h3>
                
                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Keywords
                  </label>
                  <input 
                    type="text" 
                    placeholder="Enter keywords..."
                    className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-urban mb-2">
                    Sort By
                  </label>
                  <select className="w-full px-3 py-2 border border-urban/20 rounded-lg focus:ring-2 focus:ring-cardboard focus:border-transparent">
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="distance">Distance</option>
                    <option value="popularity">Most Popular</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-urban/20 text-cardboard focus:ring-cardboard"
                    />
                    <span className="ml-2 text-sm text-urban">Has Photos</span>
                  </label>
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-urban/20 text-cardboard focus:ring-cardboard"
                    />
                    <span className="ml-2 text-sm text-urban">AI Analyzed</span>
                  </label>
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-urban/20 text-cardboard focus:ring-cardboard"
                    />
                    <span className="ml-2 text-sm text-urban">Recently Updated</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="mt-8 text-center">
              <button className="bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 hover:shadow-md active:scale-95">
                Search with Advanced Filters
              </button>
            </div>
          </div>

          {/* Feature Flag Debug Info */}
          {import.meta.env.DEV && (
            <div className="mt-8 bg-white rounded-lg p-4 border border-urban/10">
              <h3 className="text-sm font-semibold text-urban mb-2">Feature Flag Debug</h3>
              <p className="text-xs text-urban/70">
                This page is controlled by the ADVANCED_SEARCH_ENABLED feature flag.
                It will only be visible when the flag is enabled.
              </p>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}